import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CheckIcon from '@mui/icons-material/Check';

const PricingSection = ({ 
  showTitle = true, 
  showDescription = true,
  containerMaxWidth = "xl",
  buttonText = "Get Started",
  onPlanSelect // Optional callback for plan selection
}) => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  // Handle billing period toggle
  const handleBillingChange = (event, newBillingPeriod) => {
    if (newBillingPeriod !== null) {
      setBillingPeriod(newBillingPeriod);
    }
  };

  // Handle Stripe checkout
  const handleCheckout = async (plan) => {
    setCheckoutLoading(plan);
    
    // If there's a custom onPlanSelect callback, use it
    if (onPlanSelect) {
      onPlanSelect(plan, billingPeriod);
      setCheckoutLoading(null);
      return;
    }

    // Default behavior - direct checkout
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/create-checkout-session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan: plan,
          billing_period: billingPeriod
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Redirect to signup section if on landing page, or to login
          const signupSection = document.getElementById('signup-section');
          if (signupSection) {
            signupSection.scrollIntoView({ behavior: 'smooth' });
          } else {
            window.location.href = '/login';
          }
          return;
        }
        
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const { checkout_url } = await response.json();
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <Box sx={{ 
      py: showTitle || showDescription ? { xs: 8, md: 10 } : { xs: 2, md: 3 }, 
      backgroundColor: '#f8f9fe' 
    }}>
      <Container maxWidth={containerMaxWidth}>
        {showTitle && (
          <Typography variant="h3" align="center" sx={{ mb: 2, fontWeight: 700, color: '#0A2540' }}>
            Enterprise-Level Insights at Small Business Prices
          </Typography>
        )}
        {showDescription && (
          <Typography variant="h6" align="center" sx={{ color: '#546E7A', mb: 6, fontWeight: 400, fontSize: '1.15rem', maxWidth: '90%', mx: 'auto' }}>
            10x cheaper than existing solutions. No contracts, no commitments - pay monthly and cancel anytime.
          </Typography>
        )}

        {/* Billing Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <ToggleButtonGroup
            value={billingPeriod}
            exclusive
            onChange={handleBillingChange}
            sx={{
              backgroundColor: 'white',
              borderRadius: '50px',
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '50px !important',
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                '&.Mui-selected': {
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1565C0',
                  }
                },
                '&:not(.Mui-selected)': {
                  color: '#546E7A',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  }
                }
              }
            }}
          >
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="yearly">
              Yearly
              <Chip
                label="25% SAVING"
                size="small"
                sx={{
                  ml: 1,
                  backgroundColor: '#ff4444',
                  color: 'white',
                  fontSize: '0.7rem',
                  height: '20px'
                }}
              />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Pricing Cards */}
        <Grid container columns={16} spacing={2} justifyContent="center">
          {/* Solo Plan */}
          <Grid gridColumn="span 16 span 8@md span 4@lg">
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                width: '100%',
                minWidth: '280px',
                maxWidth: '320px',
                mx: 'auto',
                borderRadius: '20px',
                backgroundColor: 'white',
                border: '1px solid rgba(25, 118, 210, 0.1)',
                boxShadow: '0 8px 32px -12px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 16px 40px -12px rgba(0,0,0,0.15)'
                }
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0A2540', mb: 1 }}>
                Solo
              </Typography>
              <Typography variant="body2" sx={{ color: '#546E7A', mb: 3 }}>
                Perfect for single location businesses
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h3" component="span" sx={{ fontWeight: 800, color: '#1976d2' }}>
                  ${billingPeriod === 'monthly' ? '12' : '9'}
                </Typography>
                <Typography component="span" sx={{ color: '#546E7A', fontSize: '1rem' }}>
                  /month
                </Typography>
                {billingPeriod === 'yearly' && (
                  <Typography variant="body2" sx={{ color: '#546E7A', mt: 0.5, display: 'block' }}>
                    Billed as $108/year
                  </Typography>
                )}
                {billingPeriod === 'yearly' && (
                  <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 600, display: 'block' }}>
                    Save $36 yearly (25% off)
                  </Typography>
                )}
              </Box>

              <List sx={{ mb: 4, p: 0, flexGrow: 1 }}>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="1 business location" secondary={`$${billingPeriod === 'monthly' ? '12' : '9'} per location`} primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }} secondaryTypographyProps={{ fontSize: '0.8rem', color: '#546E7A' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="No account connections required" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Quantitative revenue impact analysis" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Multi-platform review analysis" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Personalised support" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="No contracts - cancel anytime" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
              </List>

              <Button
                variant="contained"
                fullWidth
                disabled={checkoutLoading === 'solo'}
                sx={{
                  py: 1.5,
                  borderRadius: '50px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565C0 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                  }
                }}
                onClick={() => handleCheckout('solo')}
              >
                {checkoutLoading === 'solo' ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Get Started'
                )}
              </Button>
            </Paper>
          </Grid>

          {/* Starter Plan */}
          <Grid gridColumn="span 16 span 8@md span 4@lg">
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                width: '100%',
                minWidth: '280px',
                maxWidth: '320px',
                mx: 'auto',
                borderRadius: '20px',
                backgroundColor: 'white',
                border: '1px solid rgba(25, 118, 210, 0.1)',
                boxShadow: '0 8px 32px -12px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 16px 40px -12px rgba(0,0,0,0.15)'
                }
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0A2540', mb: 1 }}>
                Starter
              </Typography>
              <Typography variant="body2" sx={{ color: '#546E7A', mb: 3 }}>
                Perfect for small businesses
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h3" component="span" sx={{ fontWeight: 800, color: '#1976d2' }}>
                  ${billingPeriod === 'monthly' ? '29' : '22'}
                </Typography>
                <Typography component="span" sx={{ color: '#546E7A', fontSize: '1rem' }}>
                  /month
                </Typography>
                {billingPeriod === 'yearly' && (
                  <Typography variant="body2" sx={{ color: '#546E7A', mt: 0.5, display: 'block' }}>
                    Billed as $261/year
                  </Typography>
                )}
                {billingPeriod === 'yearly' && (
                  <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 600, display: 'block' }}>
                    Save $87 yearly (25% off)
                  </Typography>
                )}
              </Box>

              <List sx={{ mb: 4, p: 0, flexGrow: 1 }}>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Up to 3 business locations" secondary={`$${((billingPeriod === 'monthly' ? 29 : 22) / 3).toFixed(2)} per location`} primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }} secondaryTypographyProps={{ fontSize: '0.8rem', color: '#546E7A' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="No account connections required" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Quantitative revenue impact analysis" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Multi-platform review analysis" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Personalised support" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="No contracts - cancel anytime" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
              </List>

              <Button
                variant="contained"
                fullWidth
                disabled={checkoutLoading === 'starter'}
                sx={{
                  py: 1.5,
                  borderRadius: '50px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565C0 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                  }
                }}
                onClick={() => handleCheckout('starter')}
              >
                {checkoutLoading === 'starter' ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Get Started'
                )}
              </Button>
            </Paper>
          </Grid>

          {/* Growth Plan */}
          <Grid gridColumn="span 16 span 8@md span 4@lg">
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                width: '100%',
                minWidth: '280px',
                maxWidth: '320px',
                mx: 'auto',
                borderRadius: '20px',
                backgroundColor: 'white',
                border: '1px solid rgba(25, 118, 210, 0.1)',
                boxShadow: '0 8px 32px -12px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 16px 40px -12px rgba(0,0,0,0.15)'
                }
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0A2540', mb: 1 }}>
                Growth
              </Typography>
              <Typography variant="body2" sx={{ color: '#546E7A', mb: 3 }}>
                Ideal for growing businesses
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h3" component="span" sx={{ fontWeight: 800, color: '#1976d2' }}>
                  ${billingPeriod === 'monthly' ? '89' : '67'}
                </Typography>
                <Typography component="span" sx={{ color: '#546E7A', fontSize: '1rem' }}>
                  /month
                </Typography>
                {billingPeriod === 'yearly' && (
                  <Typography variant="body2" sx={{ color: '#546E7A', mt: 0.5, display: 'block' }}>
                    Billed as $801/year
                  </Typography>
                )}
                {billingPeriod === 'yearly' && (
                  <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 600, display: 'block' }}>
                    Save $267 yearly (25% off)
                  </Typography>
                )}
              </Box>

              <List sx={{ mb: 4, p: 0, flexGrow: 1 }}>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Up to 10 business locations" secondary={`$${((billingPeriod === 'monthly' ? 89 : 67) / 10).toFixed(2)} per location`} primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }} secondaryTypographyProps={{ fontSize: '0.8rem', color: '#546E7A' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="No account connections required" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Quantitative revenue impact analysis" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Multi-platform review analysis" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Personalised support" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="No contracts - cancel anytime" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
              </List>

              <Button
                variant="contained"
                fullWidth
                disabled={checkoutLoading === 'growth'}
                sx={{
                  py: 1.5,
                  borderRadius: '50px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565C0 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                  }
                }}
                onClick={() => handleCheckout('growth')}
              >
                {checkoutLoading === 'growth' ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Get Started'
                )}
              </Button>
            </Paper>
          </Grid>

          {/* Professional Plan */}
          <Grid gridColumn="span 16 span 8@md span 4@lg">
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                width: '100%',
                minWidth: '280px',
                maxWidth: '320px',
                mx: 'auto',
                borderRadius: '20px',
                backgroundColor: 'white',
                border: '1px solid rgba(25, 118, 210, 0.1)',
                boxShadow: '0 8px 32px -12px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 16px 40px -12px rgba(0,0,0,0.15)'
                }
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0A2540', mb: 1 }}>
                Professional
              </Typography>
              <Typography variant="body2" sx={{ color: '#546E7A', mb: 3 }}>
                For large-scale operations
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h3" component="span" sx={{ fontWeight: 800, color: '#1976d2' }}>
                  ${billingPeriod === 'monthly' ? '299' : '224'}
                </Typography>
                <Typography component="span" sx={{ color: '#546E7A', fontSize: '1rem' }}>
                  /month
                </Typography>
                {billingPeriod === 'yearly' && (
                  <Typography variant="body2" sx={{ color: '#546E7A', mt: 0.5, display: 'block' }}>
                    Billed as $2,691/year
                  </Typography>
                )}
                {billingPeriod === 'yearly' && (
                  <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 600, display: 'block' }}>
                    Save $897 yearly (25% off)
                  </Typography>
                )}
              </Box>

              <List sx={{ mb: 4, p: 0, flexGrow: 1 }}>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Up to 100 business locations" secondary={`$${((billingPeriod === 'monthly' ? 299 : 224) / 100).toFixed(2)} per location`} primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }} secondaryTypographyProps={{ fontSize: '0.8rem', color: '#546E7A' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="No account connections required" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Quantitative revenue impact analysis" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Multi-platform review analysis" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Personalised support" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="No contracts - cancel anytime" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                </ListItem>
              </List>

              <Button
                variant="contained"
                fullWidth
                disabled={checkoutLoading === 'professional'}
                sx={{
                  py: 1.5,
                  borderRadius: '50px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565C0 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                  }
                }}
                onClick={() => handleCheckout('professional')}
              >
                {checkoutLoading === 'professional' ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Get Started'
                )}
              </Button>
            </Paper>
          </Grid>

          {/* Custom Plan */}
          <Grid gridColumn="span 16">
            <Box sx={{ textAlign: 'center', mt: 6, p: 4, backgroundColor: 'rgba(25, 118, 210, 0.02)', borderRadius: '16px' }}>
              <Typography variant="h6" sx={{ color: '#0A2540', mb: 2, fontWeight: 600 }}>
                Need more than 100 locations or would like custom integrations?
              </Typography>
              <Button
                variant="outlined"
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: '50px',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    borderColor: '#1565C0',
                  }
                }}
                href="mailto:info@sashy.ai"
              >
                Contact Us
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PricingSection; 